const BASE_DOMAINS = [
  "d8simw-gd.myshopify.com",
  "d8simw-gd.com",
  "store.d8simw-gd.com",
  "seedsbydesign.myshopify.com",
  "seedsbydesign.com",
  "store.seedsbydesign.com",
];
const INTERNAL_DOMAINS = [
  ...BASE_DOMAINS,
  ...BASE_DOMAINS.map((domain) => `https://${domain}`),
];

function maybeTruncate(text, limit, noEllipsis) {
  if (text.length < limit) return text;

  let buffer = 0;
  let truncated = text.slice(0, limit);
  const lastSpace = truncated.lastIndexOf(" ");
  const finalChar = text.charAt(lastSpace - 1);
  const punctuation = [
    ",",
    ".",
    "!",
    "?",
    ";",
    ":",
    "-",
    "—",
    "–",
    "(",
    ")",
    "[",
    "]",
    "{",
    "}",
    '"',
    "'",
    "“",
    "”",
    "‘",
    "’",
  ];

  if (punctuation.includes(finalChar)) {
    buffer = 1;
  }

  truncated = text.slice(0, lastSpace - buffer) + (noEllipsis ? "" : "...");

  return truncated;
}

/**
 * Validates that a url is local
 * @param url
 * @returns `true` if local `false`if external domain
 */
function isLocalPath(url) {
  try {
    // We don't want to redirect cross domain,
    // doing so could create fishing vulnerability
    // If `new URL()` succeeds, it's a fully qualified
    // url which is cross domain. If it fails, it's just
    // a path, which will be the current domain.
    new URL(url);
  } catch (e) {
    return true;
  }

  return false;
}

function isInternalUrl(url) {
  if (isLocalPath(url)) return true;

  try {
    const { hostname } = new URL(url, location.origin);
    return INTERNAL_DOMAINS.some(
      (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
    );
  } catch {
    return true;
  }
}

function externalLinkHandler(linkElement) {
  const rawHref =
    linkElement.getAttribute("href") || linkElement.getAttribute("to");

  let parsedLink = {
    isExternal: false,
    to: rawHref ?? "/",
    target: "_self",
    rel: undefined,
    innerText: "",
    ariaLabel: undefined,
    alt: undefined,
    labelType: undefined,
    rawAppendedLabel: "",
    parsedSuccesfully: false,
  };

  if (!rawHref) {
    return parsedLink;
  }

  const link = ADASanitizeActionElTitle(linkElement);
  const isInternalLink = isInternalUrl(rawHref);
  const linkPartial = "links to";
  const blankPartial = "opens in a new tab";
  const externalPartial = `external ${blankPartial}`;

  const previewImgPartial = "image preview";

  let pathname = rawHref;

  if (isInternalLink) {
    if (!isLocalPath(rawHref)) {
      try {
        const relative = new URL(rawHref);
        pathname = relative.pathname + relative.search + relative.hash;
      } catch {
        pathname = rawHref;
      }
    }
  } else {
    pathname = rawHref;
  }

  let appendedLabel = "";
  let altText = "";
  let blankLabel = blankPartial;
  let fullLabel = "";
  let labelType = "auto";

  const cleanedPath = pathname.trim().replace(/https?:\/\//, "");
  const isAccountPath =
    cleanedPath.startsWith("account.") ||
    /shopify\.com\/[0-9]/.test(cleanedPath) ||
    cleanedPath.includes("/account/");
  const existingBlank = link.getAttribute("target") === "_blank";

  const img =
    link.querySelector("img") ||
    link.querySelector("i") ||
    link.querySelector("svg");
  const isIconImg =
    img &&
    (img.tagName.toLowerCase() === "i" || img.tagName.toLowerCase() === "svg");

  const innerText = link.textContent?.trim() || link.innerText?.trim() || "";
  const hasInnerText = innerText !== "" && !isIconImg;

  const isImgOnlyLink = img && !hasInnerText;

  const existingAlt = img ? img.getAttribute("alt")?.trim() || "" : "";
  const hasAlt = existingAlt !== "";
  const agnosticAlt = existingAlt.toLowerCase();

  if (img) {
    if (isIconImg) {
      if (hasAlt) {
        if (!link.getAttribute("aria-label")?.trim()) {
          link.setAttribute("aria-label", agnosticAlt);
        }
        img.removeAttribute("alt");
      }
      img.setAttribute("aria-hidden", "true");
    } else {
      if (hasAlt) {
        altText = agnosticAlt;
      } else {
        altText = ADASanitizeAltText(img);
      }
    }
  }

  const existingLabel = link.getAttribute("aria-label")?.trim() || "";
  const hasAriaLabel = existingLabel !== "";
  const agnosticAria = existingLabel.toLowerCase();

  if (!isInternalLink) {
    blankLabel = externalPartial;
  } else if (existingBlank || isAccountPath) {
    blankLabel = blankPartial;
  }

  if (isImgOnlyLink) {
    if (cleanedPath.includes("/cdn/")) {
      link.setAttribute("target", "_blank");
      if (hasAlt) {
        if (!agnosticAlt.includes(previewImgPartial)) {
          appendedLabel = `${previewImgPartial} (${blankPartial})`;
        }
        fullLabel = `${altText} ${appendedLabel}`;
        labelType = "alt";
      } else if (hasAriaLabel) {
        if (!agnosticAria.includes(previewImgPartial)) {
          appendedLabel = `${previewImgPartial} (${blankPartial})`;
        }
        fullLabel = `${agnosticAria} ${appendedLabel}`;
        labelType = "aria";
      } else {
        fullLabel = `${previewImgPartial} (${blankPartial})`;
        labelType = "auto";
      }
    } else if (!isInternalLink || existingBlank || isAccountPath) {
      if (hasAlt) {
        if (!agnosticAlt.includes(blankPartial)) {
          appendedLabel = `(${blankLabel})`;
        }
        if (!agnosticAlt.includes(linkPartial)) {
          fullLabel = `${altText} ${linkPartial} ${cleanedPath} ${appendedLabel}`;
        } else {
          fullLabel = `${altText} ${appendedLabel}`;
        }
        labelType = "alt";
      } else if (hasAriaLabel) {
        if (!agnosticAria.includes(blankPartial)) {
          appendedLabel = `(${blankLabel})`;
        }
        if (!agnosticAria.includes(linkPartial)) {
          fullLabel = `${agnosticAria} ${linkPartial} ${cleanedPath} ${appendedLabel}`;
        } else {
          fullLabel = `${agnosticAria} ${appendedLabel}`;
        }
        labelType = "aria";
      } else {
        fullLabel = `${linkPartial} ${cleanedPath} (${blankLabel})`;
        labelType = "auto";
      }
    } else {
      if (hasAlt) {
        if (!agnosticAlt.includes(linkPartial)) {
          fullLabel = `${altText} ${linkPartial} ${cleanedPath}`;
        } else {
          fullLabel = altText;
        }
        labelType = "alt";
      } else if (hasAriaLabel) {
        if (!agnosticAria.includes(linkPartial)) {
          fullLabel = `${agnosticAria} ${linkPartial} ${cleanedPath}`;
        } else {
          fullLabel = agnosticAria;
        }
        labelType = "aria";
      } else {
        fullLabel = `${linkPartial} ${cleanedPath}`;
        labelType = "auto";
      }
    }
  } else {
    if (!isInternalLink || existingBlank || isAccountPath) {
      if (hasInnerText) {
        if (hasAriaLabel) {
          if (!agnosticAria.includes(blankPartial)) {
            appendedLabel = `${existingLabel} (${blankLabel})`;
          } else {
            appendedLabel = existingLabel;
          }
          fullLabel = innerText; // use raw inner, append in the sanitization function
        } else {
          appendedLabel = `(${blankLabel})`;
          fullLabel = innerText; // use raw inner, append in the sanitization function
        }
        labelType = "text";
      } else if (hasAriaLabel) {
        if (!agnosticAria.includes(blankPartial)) {
          appendedLabel = `(${blankLabel})`;
        }
        fullLabel = `${agnosticAria} ${appendedLabel}`;
        labelType = "aria";
      } else if (img) {
        if (!isIconImg) {
          if (hasAlt) {
            if (!agnosticAlt.includes(blankPartial)) {
              appendedLabel = `(${blankLabel})`;
            }
          } else {
            altText = ADASanitizeAltText(img);
            if (!altText.includes(blankPartial)) {
              appendedLabel = `(${blankLabel})`;
            }
          }
          fullLabel = `${altText} ${appendedLabel}`;
          labelType = "alt";
        } else {
          if (hasAriaLabel) {
            if (!agnosticAria.includes(blankPartial)) {
              appendedLabel = `(${blankLabel})`;
            }
            fullLabel = `${agnosticAria} ${appendedLabel}`;
            labelType = "aria";
          } else {
            fullLabel = `${linkPartial} ${cleanedPath} (${blankLabel})`;
            labelType = "auto";
          }
        }
      } else {
        fullLabel = `${linkPartial} ${cleanedPath} (${blankLabel})`;
        labelType = "auto";
      }
    } else {
      if (hasInnerText) {
        fullLabel = innerText;
        labelType = "text";
      } else if (hasAriaLabel) {
        fullLabel = agnosticAria;
        labelType = "aria";
      } else if (img) {
        if (!isIconImg) {
          if (!hasAlt) {
            altText = ADASanitizeAltText(img);
          }
          fullLabel = altText;
          labelType = "alt";
        } else {
          if (hasAriaLabel) {
            fullLabel = agnosticAria;
            labelType = "aria";
          } else {
            fullLabel = `${linkPartial} ${cleanedPath}`;
            labelType = "auto";
          }
        }
      } else {
        fullLabel = `${linkPartial} ${cleanedPath}`;
        labelType = "auto";
      }
    }
  }

  parsedLink = {
    isExternal: !isInternalLink,
    to: pathname,
    target:
      isInternalLink && !existingBlank && !isAccountPath ? "_self" : "_blank",
    rel: isInternalLink
      ? existingBlank || isAccountPath
        ? "noopener"
        : undefined
      : "noopener noreferrer",
    innerText: labelType === "text" ? fullLabel : undefined,
    ariaLabel: labelType === "aria" ? fullLabel : undefined,
    alt: labelType === "alt" ? fullLabel : undefined,
    autoLabel: labelType === "auto" ? fullLabel : undefined,
    rawAppendedLabel: appendedLabel,
    parsedSuccesfully: true,
  };

  return parsedLink;
}

function isEmptyHeading(heading) {
  return !!(
    !heading.innerText ||
    heading.innerText.trim() === "" ||
    !heading.innerHTML
  );
}

function getHeadingLevel(heading) {
  if (!heading) return null;
  return parseInt(heading.tagName.substring(1));
}

function replaceHeading(headingEl, newEl) {
  newEl.innerHTML = headingEl.innerHTML;
  for (const attr of headingEl.attributes) {
    newEl.setAttribute(attr.name, attr.value);
  }
  headingEl.replaceWith(newEl);
}

function ADASanitizeHeadings(blockSelector) {
  const globalH1 = document.querySelector('h1#primary-h1') || document.querySelector('body > h1');
  const block = document.querySelector(blockSelector);
  if (!block) return;

  const headings = Array.from(block.querySelectorAll("h1, h2, h3, h4, h5, h6"));
  let previousLevel = null;
  let firstHeadingProcessed = false;

  headings.forEach((heading) => {
    if (isEmptyHeading(heading)) {
      const newDiv = document.createElement("div");
      replaceHeading(heading, newDiv);
      return;
    }

    let currentLevel = parseInt(heading.tagName.substring(1));
    if (!firstHeadingProcessed) {
      if (globalH1 && currentLevel === 1) {
        shiftHeading(heading, 2);
        previousLevel = 2;
      } else {
        previousLevel = currentLevel;
      }
      firstHeadingProcessed = true;
      return;
    }

    if (previousLevel !== null && currentLevel > previousLevel + 1) {
      const newLevel = previousLevel + 1;
      shiftHeading(heading, newLevel);
      previousLevel = newLevel;
    } else {
      previousLevel = currentLevel;
    }
  })
}

function shiftHeading(headingEl, newLevel) {
  const currentLevel = parseInt(headingEl.tagName.substring(1));
  if (currentLevel === newLevel || headingEl.classList.contains('h-level-shifted') || headingEl.id === "primary-h1") return;
  const newHeading = document.createElement(`h${newLevel}`);
  newHeading.innerHTML = headingEl.innerHTML;
  for (const attr of headingEl.attributes) {
    newHeading.setAttribute(attr.name, attr.value);
  }
  const headingColor = window.getComputedStyle(headingEl).color;
  headingEl.replaceWith(newHeading);
  newHeading.classList.add(`h${currentLevel}`);
  newHeading.classList.add('h-level-shifted');
  newHeading.style.color = headingColor;
}

function ADASanitizeActionElTitle(actionEl) {
  const actionText =
    (actionEl.innerText?.trim() || actionEl.textContent?.trim()) ?? null;
  const title = actionEl.getAttribute("title")?.trim()?.toLowerCase() ?? null;
  const iconImg = actionEl.querySelector("svg") ?? actionEl.querySelector("i");
  const img = actionEl.querySelector("img");
  const ariaLabel = actionEl.getAttribute("aria-label")?.trim() ?? null;

  if (title === "") {
    actionEl.removeAttribute("title");
    return actionEl;
  }

  if (actionText && !title) {
    return actionEl;
  }

  if (actionText && title) {
    actionEl.removeAttribute("title");
  } else if (!actionText && title) {
    if (ariaLabel) {
      actionEl.removeAttribute("title");
    } else if (!iconImg) {
      if (!img) {
        actionEl.setAttribute("aria-label", title);
      }
      actionEl.removeAttribute("title");
    }
  }

  return actionEl;
}

function ADASanitizeAltText(imageSrc) {
  let altText = "";
  const source =
    typeof imageSrc === "string"
      ? imageSrc
      : (imageSrc.getAttribute("src") ??
        imageSrc.getAttribute("data-src") ??
        imageSrc.getAttribute("data-sanitized-data-src"));
  if (!source) return "";

  const hasAlt = typeof imageSrc !== "string" && imageSrc.hasAttribute("alt");

  if (hasAlt)
    return maybeTruncate(imageSrc.getAttribute("alt") || "", 124, true);

  const filename = source.split("/").pop()?.split(".")[0] || "";
  const parts = filename.split(/[-_]/).filter((part) => {
    if (/^\d+$/.test(part)) return false;
    if (/^[a-f]+$/.test(part) && (part.includes("a") || part.includes("e")))
      return true;
    if (/^[a-f0-9]+$/.test(part)) return false;
    return true;
  });

  altText = parts.join(" ").replace(/\s+/g, " ").toLowerCase().trim();
  return maybeTruncate(altText, 124, true) || "";
}

function ADASanitizeDescriptionHtml(rootElement) {
  const underlines = rootElement.querySelectorAll(
    ".underline, [style*='text-decoration: underline']",
  );
  underlines.forEach((el) => {
    if (el.hasAttribute("href") || el.tagName.toLowerCase() === "a") return;
    el.classList.remove("underline");
    el.style.textDecoration = el.style.textDecoration.replace(/underline/g, "");
  });

  // Fix images missing alt attributes
  const images = rootElement.querySelectorAll("img");
  images.forEach((img) => {
    img.setAttribute("alt", ADASanitizeAltText(img));
  });

  // Fix images where alt duplicates adjacent text
  const imgLinks = rootElement.querySelectorAll("a");
  imgLinks.forEach((link) => {
    const img = link.querySelector("img");
    if (!img) return;

    const alt = (img.getAttribute("alt") || "").toLowerCase().trim();
    if (!alt) return;

    // Get all text content from the link, excluding img alt attributes
    const clone = link.cloneNode(true);
    clone.querySelectorAll("img").forEach((i) => i.remove());
    const linkText = clone.textContent?.toLowerCase().trim() || "";

    if (linkText) {
      img.setAttribute("alt", "");
    }
  });

  // Fix layout tables - convert to divs
  const tables = rootElement.querySelectorAll("table");
  tables.forEach((table) => {
    const hasHeaders = table.querySelector("thead, th");
    if (!hasHeaders) {
      const wrapper = document.createElement("div");
      wrapper.classList.add("ada-cleaned-table-body");

      const rows = table.querySelectorAll("tr");
      rows.forEach((row) => {
        const rowDiv = document.createElement("div");
        rowDiv.classList.add("ada-cleaned-table-row");

        const cells = row.querySelectorAll("td");
        cells.forEach((cell) => {
          const cellDiv = document.createElement("div");
          cellDiv.innerHTML = cell.innerHTML;
          if (cell.style.width) {
            cellDiv.style.width = cell.style.width;
          }
          rowDiv.appendChild(cellDiv);
        });

        wrapper.appendChild(rowDiv);
      });

      table.replaceWith(wrapper);
    }
  });

  const buttons = rootElement.querySelectorAll("button");
  buttons.forEach((button) => {
    button = ADASanitizeActionElTitle(button);
  });

  const allLinks = rootElement.querySelectorAll("a");
  allLinks.forEach((link) => {
    const rawHref = link.getAttribute("href");

    if (
      !rawHref ||
      rawHref === "" ||
      rawHref === "#" ||
      rawHref.startsWith("#") ||
      rawHref.startsWith("mailto:") ||
      rawHref.startsWith("tel:")
    )
      return;

    const parsed = externalLinkHandler(link);

    if (!parsed.parsedSuccesfully) {
      console.log("Failed to parse link:", link);
      return;
    }

    link.setAttribute("target", parsed.target);

    if (parsed.rel && parsed.rel !== "") {
      link.setAttribute("rel", parsed.rel);
    } else {
      link.removeAttribute("rel");
    }

    if (!parsed.isExternal && parsed.to && !parsed.to.startsWith("/")) {
      try {
        const relative = new URL(parsed.to, location.origin);
        if (relative.hostname === location.hostname) {
          link.setAttribute(
            "href",
            relative.pathname + relative.search + relative.hash,
          );
        }
      } catch {}
    }

    const img =
      link.querySelector("img") ||
      link.querySelector("i") ||
      link.querySelector("svg");
    const isIconImg =
      img &&
      (img.tagName.toLowerCase() === "i" ||
        img.tagName.toLowerCase() === "svg");

    const innerText = parsed.innerText || "";
    const hasInnerText = innerText !== "" && !isIconImg;

    const isImgOnlyLink = img && !hasInnerText;

    const ariaLabel = parsed.ariaLabel || "";
    const hasAriaLabel = ariaLabel !== "";

    const altText = parsed.alt || "";
    const hasAlt = altText !== "";

    const autoLabel = parsed.autoLabel || "";
    const hasAutoLabel = autoLabel !== "";

    if (isImgOnlyLink) {
      if (hasAlt) {
        img.setAttribute("alt", altText);
      } else if (hasAriaLabel) {
        img.setAttribute("alt", ariaLabel);
        img.removeAttribute("aria-label");
      } else if (hasAutoLabel) {
        img.setAttribute("alt", autoLabel);
      }
    } else {
      if (hasInnerText) {
        if (parsed.rawAppendedLabel && parsed.rawAppendedLabel !== "") {
          if (link.hasAttribute("aria-label")) {
            link.removeAttribute("aria-label");
            const srOnly = document.createElement("span");
            srOnly.className = "sr-only";
            srOnly.textContent = parsed.rawAppendedLabel;
            link.appendChild(srOnly);
          } else if (parsed.isExternal || parsed.target === "_blank") {
            const srOnly = document.createElement("span");
            srOnly.className = "sr-only";
            srOnly.textContent = parsed.rawAppendedLabel;
            link.appendChild(srOnly);
          }
        }
      } else if (hasAriaLabel) {
        link.setAttribute("aria-label", ariaLabel);
      } else {
        if (hasAutoLabel) {
          link.setAttribute("aria-label", autoLabel);
        }
      }
    }

    const adjacentLinks = rootElement.querySelectorAll("a");
    let parsedSiblingIndex = 0;
    adjacentLinks.forEach((childLink) => {
      const parsedSibling = externalLinkHandler(childLink);
      if (
        !parsedSibling.isExternal &&
        parsed.to &&
        !parsedSibling.to.startsWith("/")
      ) {
        try {
          const relative = new URL(parsedSibling.to, location.origin);
          if (relative.hostname === location.hostname) {
            childLink.setAttribute(
              "href",
              relative.pathname + relative.search + relative.hash,
            );
          }
        } catch {}
      }

      if (childLink === link || childLink.href !== link.href) return;
      parsedSiblingIndex++;
      let suffix = "#";
      if (childLink.href.includes(suffix) || parsedSiblingIndex > 1) {
        suffix = `?-ada-sibling-${parsedSiblingIndex + 1}`;
      }

      childLink.setAttribute("href", `${childLink.href}${suffix}`);
    });

    if (img) {
      if (!isIconImg) {
        if (img.hasAttribute("alt")) {
          img.setAttribute(
            "alt",
            maybeTruncate(img.getAttribute("alt"), 124, true),
          );
        } else if (
          img.hasAttribute("aria-label") &&
          !img.hasAttribute("aria-hidden")
        ) {
          img.setAttribute(
            "aria-label",
            maybeTruncate(img.getAttribute("aria-label"), 149, true),
          );
        }
      } else {
        if (link.hasAttribute("aria-label")) {
          link.setAttribute(
            "aria-label",
            maybeTruncate(link.getAttribute("aria-label"), 149, true),
          );
        }
      }
    } else if (link.hasAttribute("aria-label")) {
      link.setAttribute(
        "aria-label",
        maybeTruncate(link.getAttribute("aria-label"), 149, true),
      );
    }
  });
}

document.addEventListener("DOMContentLoaded", function () {
  ADASanitizeHeadings(".sanitize-description-html");

  Array.from(document.getElementsByClassName("sanitize-alt-text")).forEach(
    (element) => {
      if (element.tagName.toLowerCase() === "img") {
        const alt = ADASanitizeAltText(element);
        element.setAttribute("alt", alt);
      }
    },
  );

  Array.from(
    document.getElementsByClassName("sanitize-action-el-title"),
  ).forEach((element) => {
    if (element.tagName.toLowerCase() === "a") {
      ADASanitizeActionElTitle(element);
    }
  });

  const sanitizationTargets = document.querySelectorAll(
    ".shopify-section, .cart-drawer, .sanitize-description-html, modal-dialog",
  );
  let targetElements = [];

  for (const target of sanitizationTargets) {
    let filteredElements = [];

    if (
      target.classList.contains("sanitize-description-html") &&
      target.childNodes.length
    ) {
      Array.from(target.childNodes).forEach((child) => {
        if (
          child.nodeType === Node.ELEMENT_NODE &&
          !child.classList.contains("shopify-section")
        ) {
          filteredElements.push(child);
        }
      });
    } else {
      filteredElements.push(target);
    }

    targetElements.push(...filteredElements);
  }

  if (targetElements.length) {
    targetElements.forEach((element) => {
      ADASanitizeDescriptionHtml(element);
    });
  }
});

// Expose functions to global scope for use in other scripts
window.ADASanitizeDescriptionHtml = ADASanitizeDescriptionHtml;
window.ADASanitizeAltText = ADASanitizeAltText;
window.ADASanitizeActionElTitle = ADASanitizeActionElTitle;
window.externalLinkHandler = externalLinkHandler;
window.maybeTruncate = maybeTruncate;
window.isInternalUrl = isInternalUrl;
window.isLocalPath = isLocalPath;
